import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, Card, CardContent, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem, FormControl,
  InputLabel, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Alert, Button, TableSortLabel, TextField
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const API = process.env.REACT_APP_API_URL || "";

// Hook para ordenamiento
function useSort(defaultCol = "", defaultDir = "asc") {
  const [sortCol, setSortCol] = useState(defaultCol);
  const [sortDir, setSortDir] = useState(defaultDir);
  const toggle = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };
  const sorted = (rows) => {
    if (!sortCol) return rows;
    return [...rows].sort((a, b) => {
      let va = a[sortCol] ?? "", vb = b[sortCol] ?? "";
      if (typeof va === "number" && typeof vb === "number") return sortDir === "asc" ? va - vb : vb - va;
      va = String(va); vb = String(vb);
      return sortDir === "asc" ? va.localeCompare(vb, undefined, {numeric: true}) : vb.localeCompare(va, undefined, {numeric: true});
    });
  };
  return { sortCol, sortDir, toggle, sorted };
}

function SortCell({ col, label, sortCol, sortDir, toggle }) {
  return (
    <TableCell sx={{ whiteSpace: "nowrap", cursor: "pointer" }} sortDirection={sortCol === col ? sortDir : false}>
      <TableSortLabel active={sortCol === col} direction={sortCol === col ? sortDir : "asc"} onClick={() => toggle(col)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

// Utilidades de exportación
function exportCSV(rows, columns, filename) {
  const header = columns.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => {
    const val = r[c] ?? "";
    return `"${String(val).replace(/"/g, '""')}"`;
  }).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function exportPDF(rows, columns, columnLabels, title) {
  const { jsPDF } = await import("jspdf");
  await import("jspdf-autotable");
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(12);
  doc.text(title, 14, 15);
  doc.autoTable({
    startY: 22,
    head: [columnLabels],
    body: rows.map(r => columns.map(c => r[c] ?? "")),
    styles: { fontSize: 5.5, cellPadding: 1, overflow: "linebreak" },
    headStyles: { fillColor: [175, 39, 47], fontSize: 5.5, cellPadding: 1, halign: "center" },
    tableWidth: "wrap",
    margin: { left: 5, right: 5 },
  });
  doc.save(`${title}.pdf`);
}

function DownloadButtons({ rows, columns, columnLabels, title }) {
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  if (!rows || rows.length === 0) return null;
  const handleCsv = async () => {
    setLoadingCsv(true);
    try { exportCSV(rows, columns, `${title}.csv`); }
    finally { setLoadingCsv(false); }
  };
  const handlePdf = async () => {
    setLoadingPdf(true);
    try { await exportPDF(rows, columns, columnLabels, title); }
    finally { setLoadingPdf(false); }
  };
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1, justifyContent: "flex-end" }}>
      <Button size="small" startIcon={loadingCsv ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={handleCsv} disabled={loadingCsv}>CSV</Button>
      <Button size="small" startIcon={loadingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />} onClick={handlePdf} disabled={loadingPdf}>PDF</Button>
    </Box>
  );
}

function ReportesDefensa({ token }) {
  const [tab, setTab] = useState(0);
  const [distritoF, setDistritoF] = useState("");
  const [distritoL, setDistritoL] = useState("");
  const [seccion, setSeccion] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [imagenDialog, setImagenDialog] = useState(null);
  const [loadingImagen, setLoadingImagen] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(300);
  const [refreshActive, setRefreshActive] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const tabs = ["Dashboard", "Aperturas", "Sin Apertura", "Obs. Incidencias", "Reporte 1 - Apertura", "Reporte 2 - Operación", "Reporte 3 - Cuenta Votos", "Votos por Distrito", "Cierres", "Incidencias", "Visitas RG", "RG Incidencias", "RG Documentos", "Resultados"];
  const tabKeys = ["dashboard", "evento_aperturas", "sin_apertura", "obs_incidencias", "aperturas", "reportes_operacion", "cuenta_votos", "votos_distrito", "cierres", "incidencias", "visitas_rg", "rg_incidencias", "rg_documentos", "resultados"];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.append("seccion", tabKeys[tab]);
      if (distritoF) params.append("distrito_f", distritoF);
      if (distritoL) params.append("distrito_l", distritoL);
      if (seccion) params.append("seccion_num", seccion);
      const res = await fetch(`${API}/api/defensa/reportes?${params}`, { headers });
      if (!res.ok) throw new Error(await res.text());
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, distritoF, distritoL, seccion]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!refreshActive || !refreshInterval) return;
    const id = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(id);
  }, [refreshActive, refreshInterval, fetchData]);

  const verImagen = async (url) => {
    setLoadingImagen(true);
    try {
      const res = await fetch(`${API}/api/defensa/imagen?path=${encodeURIComponent(url)}`, { headers });
      if (!res.ok) throw new Error("Error cargando imagen");
      const blob = await res.blob();
      setImagenDialog(URL.createObjectURL(blob));
    } catch (e) {
      setError("No se pudo cargar la imagen: " + e.message);
    } finally {
      setLoadingImagen(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box", px: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>🛡️ Reportes Defensa</Typography>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: { xs: 0 }, flex: { xs: "1 1 calc(50% - 8px)", sm: "0 0 170px" } }}>
          <InputLabel>Distrito Federal</InputLabel>
          <Select value={distritoF} label="Distrito Federal" onChange={e => setDistritoF(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {[...Array(8)].map((_, i) => <MenuItem key={i+1} value={i+1}>{i+1}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: 0 }, flex: { xs: "1 1 calc(50% - 8px)", sm: "0 0 140px" } }}>
          <InputLabel>Distrito Local</InputLabel>
          <Select value={distritoL} label="Distrito Local" onChange={e => setDistritoL(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {[...Array(16)].map((_, i) => <MenuItem key={i+1} value={i+1}>{i+1}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Sección" type="number" value={seccion}
          onChange={e => setSeccion(e.target.value)} sx={{ flex: { xs: "1 1 calc(50% - 48px)", sm: "0 0 100px" } }}
          onKeyDown={e => { if (e.key === "Enter") fetchData(); }}
        />
        <IconButton onClick={fetchData} color="primary"><RefreshIcon /></IconButton>
        <TextField size="small" label="Min" type="number" value={Math.floor(refreshInterval / 60)}
          onChange={e => setRefreshInterval(Math.max(0, Number(e.target.value)) * 60 + (refreshInterval % 60))}
          sx={{ flex: "0 0 60px" }} inputProps={{ min: 0 }}
        />
        <TextField size="small" label="Seg" type="number" value={refreshInterval % 60}
          onChange={e => setRefreshInterval(Math.floor(refreshInterval / 60) * 60 + Math.max(0, Math.min(59, Number(e.target.value))))}
          sx={{ flex: "0 0 60px" }} inputProps={{ min: 0, max: 59 }}
        />
        <Button size="small" variant={refreshActive ? "contained" : "outlined"} color={refreshActive ? "success" : "inherit"}
          onClick={() => setRefreshActive(!refreshActive)} sx={{ minWidth: 70, fontSize: "0.75rem" }}>
          {refreshActive ? "Auto ⏵" : "Pausa ⏸"}
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ mb: 2, "& .MuiTab-root": { minWidth: { xs: 60, sm: 90 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.875rem" } } }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <>
          {tab === 0 && <DashboardView data={data} />}
          {tab === 1 && <EventoAperturasView data={data} />}
          {tab === 2 && <SinAperturaView data={data} />}
          {tab === 3 && <ObsIncidenciasView data={data} />}
          {tab === 4 && <AperturasView data={data} />}
          {tab === 5 && <ReportesOpView data={data} />}
          {tab === 6 && <CuentaVotosView data={data} />}
          {tab === 7 && <VotosDistritoView data={data} />}
          {tab === 8 && <CierresView data={data} verImagen={verImagen} />}
          {tab === 9 && <IncidenciasView data={data} verImagen={verImagen} />}
          {tab === 10 && <VisitasRGView data={data} />}
          {tab === 11 && <RGIncidenciasView data={data} />}
          {tab === 12 && <RGDocumentosView data={data} verImagen={verImagen} />}
          {tab === 13 && <ResultadosView data={data} />}
        </>
      )}

      <Dialog open={!!imagenDialog || loadingImagen} onClose={() => { setImagenDialog(null); setLoadingImagen(false); }} maxWidth="lg" fullWidth
        PaperProps={{ sx: { m: 1, maxHeight: "calc(100vh - 16px)", width: "calc(100% - 16px)" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
          Evidencia
          <IconButton onClick={() => { setImagenDialog(null); setLoadingImagen(false); }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", p: 1 }}>
          {loadingImagen && <CircularProgress sx={{ mt: 4 }} />}
          {imagenDialog && !loadingImagen && <Box component="img" src={imagenDialog} sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function KPI({ label, value, color }) {
  return (
    <Card sx={{ minWidth: 0, width: "100%" }}>
      <CardContent sx={{ textAlign: "center", py: 1, px: 1, "&:last-child": { pb: 1 } }}>
        <Typography variant="h5" sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem" } }} color={color || "primary"}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
      </CardContent>
    </Card>
  );
}

function DistritoTable({ rows }) {
  const cols = ["distrito_l","aperturas","reporte_1","reporte_2","reporte_3","cierres","incidencias","visita_1","visita_2","visita_3","rc_part","rc_reg","rg_part","rg_reg"];
  const labels = ["DL","Aperturas","Reporte 1°","Reporte 2°","Reporte 3°","Cierres","Incidencias","Visita RG 1°","Visita RG 2°","Visita RG 3°","RC Participando","RC Registrados","RG Participando","RG Registrados"];
  const { sortCol, sortDir, toggle, sorted } = useSort("distrito_l");
  const sortedRows = sorted(rows);
  const totales = cols.reduce((acc, c) => {
    acc[c] = c === "distrito_l" ? "TOTAL" : rows.reduce((s, r) => s + (Number(r[c]) || 0), 0);
    return acc;
  }, {});
  return (
    <Grid item xs={12}>
      <Paper sx={{ p: { xs: 1, sm: 2 }, overflow: "hidden" }}>
        <DownloadButtons rows={[...rows, totales]} columns={cols} columnLabels={labels} title="Dashboard_Defensa" />
        <Typography variant="subtitle1" gutterBottom>Por Distrito</Typography>
        <TableContainer sx={{ maxWidth: "100%" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((r, i) => (
                <TableRow key={i}>
                  {cols.map(c => <TableCell key={c}>{r[c]}</TableCell>)}
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: "action.hover", fontWeight: "bold" }}>
                {cols.map(c => <TableCell key={c} sx={{ fontWeight: "bold" }}>{totales[c]}</TableCell>)}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Grid>
  );
}

function DashboardView({ data }) {
  const d = data;
  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Aperturas" value={d.total_aperturas || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Reporte 1°" value={d.total_reporte_1 || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Reporte 2°" value={d.total_reporte_2 || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Reporte 3°" value={d.total_reporte_3 || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Cierres" value={d.total_cierres || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Incidencias" value={d.total_incidencias || 0} color="error" /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Visita RG 1°" value={d.visitas_rg_1 || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Visita RG 2°" value={d.visitas_rg_2 || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Visita RG 3°" value={d.visitas_rg_3 || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="Docs Cierre" value={d.total_docs_cierre || 0} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="RC Activos" value={`${d.rc_participando || 0}/${d.rc_registrados || 0}`} /></Grid>
        <Grid item xs={4} sm={3} md={1.5}><KPI label="RG Activos" value={`${d.rg_participando || 0}/${d.rg_registrados || 0}`} /></Grid>
      </Grid>
      <Grid container spacing={1}>
      {d.por_distrito && d.por_distrito.length > 0 && <DistritoTable rows={d.por_distrito} />}
      </Grid>
    </Box>
  );
}

function ObsIncidenciasView({ data }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","rc_nombre","rc_telefono","rg_nombre","rg_telefono","ruta_id","hora","origen","observacion"];
  const labels = ["Secc","DF","DL","Casilla","RC","Tel RC","RG","Tel RG","Ruta","Hora","Origen","Observación"];
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(rows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={rows} columns={cols} columnLabels={labels} title="Obs_Posibles_Incidencias" />
      <Alert severity="warning" sx={{ mb: 1 }}>Observaciones con posible incidencia ({rows.length})</Alert>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.rc_nombre ?? "—"}</TableCell><TableCell>{r.rc_telefono ?? "—"}</TableCell>
                <TableCell>{r.rg_nombre ?? "—"}</TableCell><TableCell>{r.rg_telefono ?? "—"}</TableCell>
                <TableCell>{r.ruta_id ?? "—"}</TableCell>
                <TableCell>{r.hora ?? "—"}</TableCell>
                <TableCell><Chip label={r.origen} size="small" color={r.origen === "Evento Apertura" ? "warning" : "info"} /></TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.observacion}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function EventoAperturasView({ data }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","rc_nombre","rc_telefono","rg_nombre","rg_telefono","ruta_id","hora_apertura","mesa_directiva_completa","ubicacion_correcta","firma_boletas","observaciones"];
  const labels = ["Secc","DF","DL","Casilla","RC","Tel RC","RG","Tel RG","Ruta","Hora Apertura","Mesa OK","Ubic OK","Firma","Observaciones"];
  const exportRows = rows.map(r => ({...r, mesa_directiva_completa: r.mesa_directiva_completa?"Sí":"No", ubicacion_correcta: r.ubicacion_correcta?"Sí":"No", firma_boletas: r.firma_boletas?"Sí":"No"}));
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(rows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Evento_Aperturas" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.rc_nombre ?? "—"}</TableCell><TableCell>{r.rc_telefono ?? "—"}</TableCell>
                <TableCell>{r.rg_nombre ?? "—"}</TableCell><TableCell>{r.rg_telefono ?? "—"}</TableCell>
                <TableCell>{r.ruta_id ?? "—"}</TableCell>
                <TableCell>{r.hora_apertura}</TableCell>
                <TableCell>{r.mesa_directiva_completa ? "✅" : "❌"}</TableCell>
                <TableCell>{r.ubicacion_correcta ? "✅" : "❌"}</TableCell>
                <TableCell>{r.firma_boletas ? "✅" : "❌"}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{r.observaciones}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function SinAperturaView({ data }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","rc_nombre","rc_telefono","rg_nombre","rg_telefono","ruta_id"];
  const labels = ["Secc","DF","DL","Casilla","RC","Teléfono RC","RG","Teléfono RG","Ruta"];
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(rows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={rows} columns={cols} columnLabels={labels} title="Sin_Apertura" />
      <Alert severity="warning" sx={{ mb: 1 }}>Casillas que NO han registrado apertura ({rows.length})</Alert>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.rc_nombre ?? "—"}</TableCell><TableCell>{r.rc_telefono ?? "—"}</TableCell>
                <TableCell>{r.rg_nombre ?? "—"}</TableCell><TableCell>{r.rg_telefono ?? "—"}</TableCell>
                <TableCell>{r.ruta_id ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function AperturasView({ data }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","rc_nombre","rc_telefono","ruta_id","hora_apertura","mesa_directiva_completa","ubicacion_correcta","firma_boletas","tinta_funciona","representantes_presentes","hora_instalacion","hora_inicio_votacion","hubo_sustitucion","boletas_contadas","boletas_cantidad","boletas_firmadas","observaciones"];
  const labels = ["Secc","DF","DL","Casilla","RC","Teléfono RC","Ruta","Hora","Mesa OK","Ubic OK","Firma","Tinta OK","Reps Presentes","Hora Instal","Hora Inicio Vot","Sustitución","Boletas Contadas","Boletas Cant","Boletas Firmadas","Observaciones"];
  const exportRows = rows.map(r => ({...r, mesa_directiva_completa: r.mesa_directiva_completa?"Sí":"No", ubicacion_correcta: r.ubicacion_correcta?"Sí":"No", firma_boletas: r.firma_boletas?"Sí":"No", tinta_funciona: r.tinta_funciona==null?"—":r.tinta_funciona?"Sí":"No", hubo_sustitucion: r.hubo_sustitucion==null?"—":r.hubo_sustitucion?"Sí":"No", boletas_contadas: r.boletas_contadas==null?"—":r.boletas_contadas?"Sí":"No", boletas_firmadas: r.boletas_firmadas==null?"—":r.boletas_firmadas?"Sí":"No"}));
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(rows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Reporte_1_Apertura" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.rc_nombre ?? "—"}</TableCell><TableCell>{r.rc_telefono ?? "—"}</TableCell><TableCell>{r.ruta_id ?? "—"}</TableCell>
                <TableCell>{r.hora_apertura}</TableCell>
                <TableCell>{r.mesa_directiva_completa ? "✅" : "❌"}</TableCell>
                <TableCell>{r.ubicacion_correcta ? "✅" : "❌"}</TableCell>
                <TableCell>{r.firma_boletas ? "✅" : "❌"}</TableCell>
                <TableCell>{r.tinta_funciona == null ? "—" : r.tinta_funciona ? "✅" : "❌"}</TableCell>
                <TableCell>{r.representantes_presentes ?? "—"}</TableCell>
                <TableCell>{r.hora_instalacion ?? "—"}</TableCell>
                <TableCell>{r.hora_inicio_votacion ?? "—"}</TableCell>
                <TableCell>{r.hubo_sustitucion == null ? "—" : r.hubo_sustitucion ? "✅" : "❌"}</TableCell>
                <TableCell>{r.boletas_contadas == null ? "—" : r.boletas_contadas ? "✅" : "❌"}</TableCell>
                <TableCell>{r.boletas_cantidad ?? "—"}</TableCell>
                <TableCell>{r.boletas_firmadas == null ? "—" : r.boletas_firmadas ? "✅" : "❌"}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{r.observaciones}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function ReportesOpView({ data }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","rc_nombre","rc_telefono","ruta_id","representante_sigue","afluencia_total","hora_afluencia","hay_supervision","hora_supervision","hay_incidentes","incidente_descripcion"];
  const labels = ["Secc","DF","DL","Casilla","RC","Teléfono RC","Ruta","Sigue","Afluencia","Hora Afl","Superv","Hora Sup","Incid","Descripción"];
  const exportRows = rows.map(r => ({...r, representante_sigue: r.representante_sigue?"Sí":"No", hay_supervision: r.hay_supervision?"Sí":"No", hay_incidentes: r.hay_incidentes?"Sí":"No"}));
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(rows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Reporte_2_Operacion" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.rc_nombre ?? "—"}</TableCell><TableCell>{r.rc_telefono ?? "—"}</TableCell><TableCell>{r.ruta_id ?? "—"}</TableCell>
                <TableCell>{r.representante_sigue ? "✅" : "❌"}</TableCell>
                <TableCell>{r.afluencia_total}</TableCell>
                <TableCell>{r.hora_afluencia ?? "—"}</TableCell>
                <TableCell>{r.hay_supervision ? "✅" : "❌"}</TableCell>
                <TableCell>{r.hora_supervision ?? "—"}</TableCell>
                <TableCell>{r.hay_incidentes ? "⚠️" : "—"}</TableCell>
                <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{r.incidente_descripcion}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function CuentaVotosView({ data }) {
  const rows = data.registros || [];
  // Obtener todos los partidos únicos
  const allPartidos = [...new Set(rows.flatMap(r => (r.partidos||[]).map(p => p.partido)))];
  const allCoaliciones = [...new Set(rows.flatMap(r => (r.coaliciones||[]).map(c => c.coalicion)))];
  // Flatten para exportar con una columna por partido
  const exportRows = rows.map(r => {
    const row = {
      seccion: r.seccion, distrito_f: r.distrito_f, distrito_l: r.distrito_l, casilla: r.casilla,
      rc_nombre: r.rc_nombre, rc_telefono: r.rc_telefono, ruta_id: r.ruta_id,
      total_votantes: r.total_votantes, votos_nulos: r.votos_nulos, boletas_sobrantes: r.boletas_sobrantes,
      hay_incidentes: r.hay_incidentes ? "Sí" : "No"
    };
    allPartidos.forEach(p => { row[`p_${p}`] = (r.partidos||[]).find(x => x.partido === p)?.votos || 0; });
    allCoaliciones.forEach(c => { row[`c_${c}`] = (r.coaliciones||[]).find(x => x.coalicion === c)?.votos || 0; });
    return row;
  });
  const cols = ["seccion","distrito_f","distrito_l","casilla","rc_nombre","rc_telefono","ruta_id","total_votantes","votos_nulos","boletas_sobrantes","hay_incidentes",
    ...allPartidos.map(p => `p_${p}`), ...allCoaliciones.map(c => `c_${c}`)];
  const labels = ["Secc","DF","DL","Casilla","RC","Teléfono RC","Ruta","Votantes","Nulos","Sobr","Incid",
    ...allPartidos.map(p => p.toUpperCase()), ...allCoaliciones.map(c => c.toUpperCase())];
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedExport = sorted(exportRows);

  // Lógica de coaliciones para resaltado
  const isMorenaWin = (r) => {
    const getVotos = (key) => Number(r[key]) || 0;
    // Coalición MORENA: MORENA + PT + coalición MORENAPT
    const morenaKeys = allPartidos.filter(p => /^(morena|pt)$/i.test(p)).map(p => `p_${p}`);
    const morenaCoalKeys = allCoaliciones.filter(c => /morena.*pt|pt.*morena|morenapt/i.test(c)).map(c => `c_${c}`);
    const sumaMorena = [...morenaKeys, ...morenaCoalKeys].reduce((s, k) => s + getVotos(k), 0);
    // Coalición PRI+UDC: PRI + UDC + coalición PRIUDC
    const priKeys = allPartidos.filter(p => /^(pri|udc)$/i.test(p)).map(p => `p_${p}`);
    const priCoalKeys = allCoaliciones.filter(c => /pri.*udc|udc.*pri|priudc/i.test(c)).map(c => `c_${c}`);
    const sumaPri = [...priKeys, ...priCoalKeys].reduce((s, k) => s + getVotos(k), 0);
    // Los demás (partidos solos que no son morena/pt/pri/udc y coaliciones que no son las dos anteriores)
    const otrosPartidos = allPartidos.filter(p => !/^(morena|pt|pri|udc)$/i.test(p)).map(p => `p_${p}`);
    const otrasCoal = allCoaliciones.filter(c => !/morena.*pt|pt.*morena|morenapt|pri.*udc|udc.*pri|priudc/i.test(c)).map(c => `c_${c}`);
    // MORENA gana si su suma es mayor a la de PRI+UDC y a cada uno de los demás
    const maxOtros = Math.max(sumaPri, ...otrosPartidos.map(k => getVotos(k)), ...otrasCoal.map(k => getVotos(k)), 0);
    return sumaMorena > maxOtros;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Reporte_3_Cuenta_Votos" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedExport.map((r, i) => {
                const guinda = isMorenaWin(r);
                return (
                  <TableRow key={i} sx={guinda ? { backgroundColor: "#800020 !important", "& td": { color: "#fff" } } : {}}>
                    {cols.map(c => <TableCell key={c}>{r[c] ?? ""}</TableCell>)}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {rows.length === 0 && <Alert severity="info">Sin datos de cuenta de votos para este filtro.</Alert>}
    </Box>
  );
}

function VotosDistritoView({ data }) {
  const rows = data.registros || [];
  const allPartidos = [...new Set(rows.flatMap(r => (r.partidos||[]).map(p => p.partido)))].sort();
  const allCoaliciones = [...new Set(rows.flatMap(r => (r.coaliciones||[]).map(c => c.coalicion)))].sort();
  const exportRows = rows.map(r => {
    const base = { distrito_l: r.distrito_l, total_votantes: r.total_votantes, votos_nulos: r.votos_nulos, boletas_sobrantes: r.boletas_sobrantes };
    allPartidos.forEach(p => { base[`p_${p}`] = (r.partidos||[]).find(x => x.partido === p)?.votos || 0; });
    allCoaliciones.forEach(c => { base[`c_${c}`] = (r.coaliciones||[]).find(x => x.coalicion === c)?.votos || 0; });
    return base;
  });
  const cols = ["distrito_l","total_votantes","votos_nulos","boletas_sobrantes",...allPartidos.map(p => `p_${p}`),...allCoaliciones.map(c => `c_${c}`)];
  const labels = ["DL","Votantes","Nulos","Sobrantes",...allPartidos.map(p => p.toUpperCase()),...allCoaliciones.map(c => c.toUpperCase())];
  const { sortCol, sortDir, toggle, sorted } = useSort("distrito_l");
  const sortedRows = sorted(exportRows);
  // Totales
  const totales = cols.reduce((acc, c) => {
    acc[c] = c === "distrito_l" ? "TOTAL" : exportRows.reduce((s, r) => s + (Number(r[c]) || 0), 0);
    return acc;
  }, {});

  const isMorenaWin = (r) => {
    const getV = (key) => Number(r[key]) || 0;
    const morenaKeys = allPartidos.filter(p => /^(morena|pt)$/i.test(p)).map(p => `p_${p}`);
    const morenaCoalKeys = allCoaliciones.filter(c => /morena.*pt|pt.*morena|morenapt/i.test(c)).map(c => `c_${c}`);
    const sumaMorena = [...morenaKeys, ...morenaCoalKeys].reduce((s, k) => s + getV(k), 0);
    const priKeys = allPartidos.filter(p => /^(pri|udc)$/i.test(p)).map(p => `p_${p}`);
    const priCoalKeys = allCoaliciones.filter(c => /pri.*udc|udc.*pri|priudc/i.test(c)).map(c => `c_${c}`);
    const sumaPri = [...priKeys, ...priCoalKeys].reduce((s, k) => s + getV(k), 0);
    const otrosP = allPartidos.filter(p => !/^(morena|pt|pri|udc)$/i.test(p)).map(p => `p_${p}`);
    const otrasC = allCoaliciones.filter(c => !/morena.*pt|pt.*morena|morenapt|pri.*udc|udc.*pri|priudc/i.test(c)).map(c => `c_${c}`);
    const maxOtros = Math.max(sumaPri, ...otrosP.map(k => getV(k)), ...otrasC.map(k => getV(k)), 0);
    return sumaMorena > maxOtros;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={[...exportRows, totales]} columns={cols} columnLabels={labels} title="Votos_Por_Distrito" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => {
              const guinda = isMorenaWin(r);
              return (
                <TableRow key={i} sx={guinda ? { backgroundColor: "#800020 !important", "& td": { color: "#fff" } } : {}}>
                  {cols.map(c => <TableCell key={c}>{r[c] ?? 0}</TableCell>)}
                </TableRow>
              );
            })}
            <TableRow sx={{ backgroundColor: "action.hover", "& td": { fontWeight: "bold" } }}>
              {cols.map(c => <TableCell key={c}>{totales[c]}</TableCell>)}
            </TableRow>
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function CierresView({ data, verImagen }) {
  const rows = data.registros || [];
  const exportRows = rows.map(r => ({
    seccion: r.seccion, distrito_f: r.distrito_f, distrito_l: r.distrito_l, casilla: r.casilla,
    hora_cierre: r.hora_cierre, observaciones: r.observaciones,
    documentos: (r.documentos||[]).map(d => d.tipo_documento).join(", ")
  }));
  const cols = ["seccion","distrito_f","distrito_l","casilla","hora_cierre","observaciones","documentos"];
  const labels = ["Secc","DF","DL","Casilla","Hora","Observaciones","Docs"];
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Cierres_Defensa" />
      {rows.map((r, i) => (
        <Accordion key={i}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ flexGrow: 1, fontSize: { xs: "0.8rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
              <strong>{r.casilla}</strong> — S{r.seccion} DF{r.distrito_f} DL{r.distrito_l} — {r.hora_cierre}
            </Typography>
            <Chip label={`${(r.documentos || []).length} docs`} size="small" sx={{ mr: 1, flexShrink: 0 }} />
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
            {r.observaciones && <Typography variant="body2" sx={{ mb: 1 }}>{r.observaciones}</Typography>}
            <Grid container spacing={1}>
              {(r.documentos || []).map((doc, j) => (
                <Grid item key={j}>
                  <Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => verImagen(doc.archivo_url)}>
                    {doc.tipo_documento}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
      {rows.length === 0 && <Alert severity="info">Sin cierres para este filtro.</Alert>}
    </Box>
  );
}

function IncidenciasView({ data, verImagen }) {
  const rows = data.registros || [];
  const [revisadas, setRevisadas] = useState({});
  const token = localStorage.getItem("token");
  const hdrs = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/api/defensa/incidencias-revisadas`, { headers: hdrs })
      .then(r => r.json()).then(setRevisadas).catch(() => {});
  }, []);// eslint-disable-line

  const toggleRevisar = async (id) => {
    if (revisadas[id]) {
      await fetch(`${API}/api/defensa/incidencia-revisar/${id}`, { method: "DELETE", headers: hdrs });
      setRevisadas(prev => { const n = {...prev}; delete n[id]; return n; });
    } else {
      await fetch(`${API}/api/defensa/incidencia-revisar/${id}`, { method: "POST", headers: hdrs });
      setRevisadas(prev => ({...prev, [id]: true}));
    }
  };

  const exportRows = rows.map(r => ({
    seccion: r.seccion, distrito_f: r.distrito_f, distrito_l: r.distrito_l, casilla: r.casilla,
    tipo: r.tipo, descripcion: r.descripcion, rol: r.rol_captura, nombre: r.nombre_captura, telefono: r.telefono_captura,
    capturado_por: r.capturado_por, created_at: r.created_at, evidencias: (r.evidencias||[]).length,
    revisada: revisadas[r.incidencia_id] ? "Sí" : "No"
  }));
  const cols = ["seccion","distrito_f","distrito_l","casilla","tipo","descripcion","rol","nombre","telefono","capturado_por","created_at","evidencias","revisada"];
  const labels = ["Secc","DF","DL","Casilla","Tipo","Descripción","Rol","Nombre","Teléfono","Usuario","Fecha","#Evid","Revisada"];
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Incidencias_Defensa" />
      {rows.map((r, i) => (
        <Accordion key={i} sx={revisadas[r.incidencia_id] ? { backgroundColor: "#e8f5e9", border: "1px solid #4caf50" } : {}}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ flexGrow: 1, fontSize: { xs: "0.8rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
              {revisadas[r.incidencia_id] && <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />}
              <strong>{r.casilla}</strong> — S{r.seccion} DF{r.distrito_f} DL{r.distrito_l}
              {r.tipo && <Chip label={r.tipo} size="small" sx={{ ml: 1 }} />}
              {r.rol_captura && <Chip label={r.rol_captura} size="small" color="info" sx={{ ml: 1 }} />}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
            <Typography variant="body2" sx={{ mb: 0.5, userSelect: "text" }}><strong>{r.casilla}</strong> — Sección {r.seccion} | DF {r.distrito_f} | DL {r.distrito_l}</Typography>
            <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-word" }}>{r.descripcion}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ userSelect: "text" }}>
              {r.rol_captura}: <strong>{r.nombre_captura || r.capturado_por}</strong> {r.telefono_captura && `| 📞 ${r.telefono_captura}`} — {r.created_at}
            </Typography>
            {r.evidencias && r.evidencias.length > 0 && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {r.evidencias.map((ev, j) => (
                  <Grid item key={j}>
                    <Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => verImagen(ev.url)}>
                      {ev.tipo}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            )}
            <Box sx={{ mt: 1.5 }}>
              <Button size="small" variant={revisadas[r.incidencia_id] ? "contained" : "outlined"} color="success"
                startIcon={<CheckCircleIcon />} onClick={() => toggleRevisar(r.incidencia_id)}>
                {revisadas[r.incidencia_id] ? "Reportada ✓" : "Marcar como Reportada"}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      {rows.length === 0 && <Alert severity="info">Sin incidencias para este filtro.</Alert>}
    </Box>
  );
}

function VisitasRGView({ data }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","rg_nombre","rg_telefono","ruta_id","tipo_recorrido","hora_llegada","hora_salida","rc_presente","observaciones"];
  const labels = ["Secc","DF","DL","Casilla","RG","Teléfono RG","Ruta","Recorrido","Llegada","Salida","RC","Observaciones"];
  const exportRows = rows.map(r => ({...r, rc_presente: r.rc_presente?"Sí":"No"}));
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(rows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Visitas_RG_Defensa" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.rg_nombre ?? "—"}</TableCell><TableCell>{r.rg_telefono ?? "—"}</TableCell><TableCell>{r.ruta_id ?? "—"}</TableCell>
                <TableCell><Chip label={r.tipo_recorrido} size="small" /></TableCell>
                <TableCell>{r.hora_llegada}</TableCell><TableCell>{r.hora_salida}</TableCell>
                <TableCell>{r.rc_presente ? "✅" : "❌"}</TableCell>
                <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{r.observaciones}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function RGIncidenciasView({ data }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","tipo_incidencia","descripcion","gravedad","created_at"];
  const labels = ["Secc","DF","DL","Casilla","Tipo","Descripción","Gravedad","Fecha"];
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(rows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={rows} columns={cols} columnLabels={labels} title="RG_Incidencias_Defensa" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.tipo_incidencia}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{r.descripcion}</TableCell>
                <TableCell><Chip label={r.gravedad || "—"} size="small" color={r.gravedad === "ALTA" ? "error" : r.gravedad === "MEDIA" ? "warning" : "default"} /></TableCell>
                <TableCell>{r.created_at}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function RGDocumentosView({ data, verImagen }) {
  const rows = data.registros || [];
  const cols = ["seccion","distrito_f","distrito_l","casilla","tipo_documento","recolectado","observaciones","created_at"];
  const labels = ["Secc","DF","DL","Casilla","Tipo Doc","Recolectado","Observaciones","Fecha"];
  const exportRows = rows.map(r => ({...r, recolectado: r.recolectado?"Sí":"No", tiene_evidencia: r.evidencia_url?"Sí":"No"}));
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={[...cols,"tiene_evidencia"]} columnLabels={[...labels,"Evidencia"]} title="RG_Documentos_Defensa" />
      {rows.map((r, i) => (
        <Accordion key={i}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ flexGrow: 1, fontSize: { xs: "0.8rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
              <strong>{r.casilla}</strong> — S{r.seccion} DF{r.distrito_f} DL{r.distrito_l} — {r.tipo_documento}
              {r.evidencia_url && <Chip label="📎" size="small" sx={{ ml: 1 }} />}
            </Typography>
            <Chip label={r.recolectado ? "Recolectado" : "No recolectado"} size="small" color={r.recolectado ? "success" : "default"} sx={{ mr: 1 }} />
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
            {r.observaciones && <Typography variant="body2" sx={{ mb: 1 }}>{r.observaciones}</Typography>}
            <Typography variant="caption" color="text.secondary">{r.created_at}</Typography>
            {r.evidencia_url && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item>
                  <Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => verImagen(r.evidencia_url)}>
                    Ver evidencia
                  </Button>
                </Grid>
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
      {rows.length === 0 && <Alert severity="info">Sin documentos RG para este filtro.</Alert>}
    </Box>
  );
}

function ResultadosView({ data }) {
  const rows = data.registros || [];
  const allPartidos = [...new Set(rows.flatMap(r => (r.partidos || []).map(p => p.partido)))].sort();
  const cols = ["seccion","distrito_f","distrito_l","casilla","total_votos","votos_nulos",...allPartidos];
  const labels = ["Secc","DF","DL","Casilla","Total Votos","Nulos",...allPartidos];
  const exportRows = rows.map(r => {
    const base = {...r};
    for (const p of allPartidos) {
      const found = (r.partidos || []).find(x => x.partido === p);
      base[p] = found ? found.votos : 0;
    }
    return base;
  });
  const { sortCol, sortDir, toggle, sorted } = useSort("seccion");
  const sortedRows = sorted(exportRows);
  return (
    <Box sx={{ width: "100%" }}>
      <DownloadButtons rows={exportRows} columns={cols} columnLabels={labels} title="Resultados_Defensa" />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c, i) => <SortCell key={c} col={c} label={labels[i]} sortCol={sortCol} sortDir={sortDir} toggle={toggle} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.seccion}</TableCell><TableCell>{r.distrito_f}</TableCell><TableCell>{r.distrito_l}</TableCell>
                <TableCell>{r.casilla}</TableCell>
                <TableCell>{r.total_votos}</TableCell><TableCell>{r.votos_nulos}</TableCell>
                {allPartidos.map(p => <TableCell key={p}>{r[p] || 0}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default ReportesDefensa;
